// Modo claro / oscuro, global y por aplicación.
//
// ⚠️ **La preferencia global va en una cookie de `.deviant.cl`, no en `localStorage`.** El portal, el
// calendario, el CRM y la página de reserva viven en subdominios distintos, y `localStorage` está
// aislado por origen: guardarlo ahí daría una preferencia "global" que no cruza de `app` a `crm`, que
// es justo lo que se pidió. La cookie es del dominio registrable, así que la ven las cuatro.
//
// La anulación por aplicación sí va en `localStorage`, porque es lo contrario: sólo aplica acá.
//
// **Qué gana a qué**: si la app tiene una preferencia propia, manda; si dice `global`, manda la
// cookie; si no hay ninguna, manda lo que prefiera el sistema operativo. Ese último escalón es el
// que hace que alguien que nunca tocó nada vea lo que ya eligió en su computador.

const COOKIE = 'dv_tema';
const VALIDOS = ['claro', 'oscuro'];

/** El nombre corto de la app, para la clave de la anulación local. */
function appActual() {
  const p = location.pathname;
  if (location.hostname.startsWith('crm.') || p.includes('crm')) return 'crm';
  // ⚠️ Los dos hosts van acá y **no** con el `calendario` de abajo, por confuso que se lea: los dos
  // sirven la pagina publica de reserva, mientras que «calendario» como clave es la app interna,
  // que vive en `app.deviant.cl/agenda.html`. Hoy la reserva es `agenda.` y `calendario.` es su 301
  // —fue al reves entre el 28 y el 29 ago 2026—, y estan los dos porque un 301 no cambia el host
  // desde el que se evalua esto si alguien tiene la pagina abierta con el nombre viejo. Sin esta
  // linea la reserva publica caeria al `return 'portal'` del final —su ruta es `/<slug>`, que no
  // dice `reservar` en ninguna parte— y usaria la preferencia de otra app, sin error.
  //
  // ⚠️ El orden importa: `agenda.` se resuelve por **host** antes de que el `p.includes('agenda')`
  // de abajo lo lea como la app interna. El calendario del negocio llega por `app.deviant.cl`, asi
  // que cae en esa segunda regla y no en esta.
  if (location.hostname.startsWith('calendario.') || location.hostname.startsWith('agenda.')
      || p.includes('reservar')) return 'reserva';
  if (p.includes('agenda')) return 'calendario';
  if (location.hostname.startsWith('admin.')) return 'admin';
  return 'portal';
}

function leerCookie(nombre) {
  const m = document.cookie.match(new RegExp('(?:^|; )' + nombre + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : null;
}

export function temaGlobal() {
  const v = leerCookie(COOKIE);
  return VALIDOS.includes(v) ? v : null;
}

export function temaDeLaApp() {
  const v = localStorage.getItem(`${COOKIE}_${appActual()}`);
  return VALIDOS.includes(v) ? v : null;
}

/** Lo que se ve, después de resolver la precedencia. */
export function temaEfectivo() {
  return temaDeLaApp()
    ?? temaGlobal()
    ?? (window.matchMedia?.('(prefers-color-scheme: light)').matches ? 'claro' : 'oscuro');
}

/**
 * El cambio de tema, con barrido en diagonal.
 *
 * Sin esto la pantalla entera pasa de negro a blanco en un fotograma, que de noche encandila. Con la
 * View Transitions API el navegador toma una foto del antes, otra del después y las cruza; acá se
 * anima el recorte para que el tema nuevo entre en diagonal, desde la esquina superior derecha.
 *
 * Se degrada solo: donde no existe la API, el tema cambia de golpe como hasta ahora. Y si el sistema
 * pide menos movimiento, no se anima — es una preferencia de accesibilidad, no una sugerencia.
 */
function conTransicion(fn) {
  const quieto = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  if (quieto || !document.startViewTransition) return fn();
  return document.startViewTransition(fn);
}

export function aplicarTema(tema = temaEfectivo()) {
  document.documentElement.dataset.tema = tema;
  // `color-scheme` es lo que hace que los controles nativos --barras de scroll, el selector de fecha,
  // el desplegable de un `<select>`-- se dibujen en el modo correcto. Sin esto, en claro salen tres
  // widgets negros en medio de una pantalla blanca y parece un error de la página.
  document.documentElement.style.colorScheme = tema === 'claro' ? 'light' : 'dark';
  return tema;
}

/**
 * Guardar la preferencia.
 *
 * `ambito` es `'global'` (cookie, todas las apps) o `'app'` (sólo ésta). Pasar `null` como tema en
 * el ámbito de la app borra la anulación y devuelve el control al global -- que es la forma de
 * decir "acá, lo que diga el resto".
 */
export function guardarTema(tema, ambito = 'global') {
  if (ambito === 'app') {
    const clave = `${COOKIE}_${appActual()}`;
    if (tema) localStorage.setItem(clave, tema);
    else localStorage.removeItem(clave);
  } else {
    // 400 días es el máximo que los navegadores respetan hoy para una cookie de script.
    // `SameSite=Lax` alcanza: no es una credencial y no viaja en peticiones de terceros.
    document.cookie = `${COOKIE}=${tema}; domain=.deviant.cl; path=/; max-age=34560000; SameSite=Lax; Secure`;
  }
  // El barrido va acá y no en `aplicarTema`: aplicar el tema también ocurre al cargar la página, y
  // animar eso sería un parpadeo en cada visita.
  let resultado;
  conTransicion(() => { resultado = aplicarTema(); });
  return resultado ?? temaEfectivo();
}

/**
 * El control de tres estados que va en cada app.
 *
 * Tres y no dos: «Claro», «Oscuro» y **«Como el resto»**, que es la opción que devuelve esta app al
 * global. Con un interruptor de dos posiciones no hay forma de deshacer una preferencia local — se
 * queda pegada para siempre y la app deja de seguir al resto sin que se entienda por qué.
 */
export function montarSelectorTema(contenedor, { ambito = 'app' } = {}) {
  if (!contenedor) return;
  const opciones = ambito === 'app'
    ? [['', 'Como el resto'], ['claro', 'Claro'], ['oscuro', 'Oscuro']]
    : [['claro', 'Claro'], ['oscuro', 'Oscuro']];
  const actual = ambito === 'app' ? (temaDeLaApp() ?? '') : (temaGlobal() ?? temaEfectivo());

  contenedor.innerHTML = '';
  const grupo = document.createElement('div');
  grupo.className = 'tema-grupo';
  grupo.setAttribute('role', 'group');
  grupo.setAttribute('aria-label', 'Tema');
  for (const [valor, texto] of opciones) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'tema-opcion' + (valor === actual ? ' activa' : '');
    b.textContent = texto;
    b.addEventListener('click', () => {
      guardarTema(valor || null, ambito);
      montarSelectorTema(contenedor, { ambito });
      contenedor.dispatchEvent(new CustomEvent('tema-cambiado', { bubbles: true }));
    });
    grupo.appendChild(b);
  }
  contenedor.appendChild(grupo);
}

/**
 * El mismo control, metido en un botón de la barra superior.
 *
 * ⚠️ **Existe porque en un teléfono no había forma de cambiar el tema.** Medido el 27 ago 2026 a 390
 * px: en el portal el bloque estaba escondido por `@media (max-width: 900px) { .pv-tema { display:
 * none } }`; en el CRM y en Marketing existía pero quedaba en `x = 444` de una barra de 390 que
 * scrollea de lado, o sea inalcanzable sin saber que hay que arrastrarla; y en el calendario y su
 * configuración no existía en ningún ancho. Cuatro aplicaciones, una sola preferencia y ningún lugar
 * donde tocarla. Un botón en la barra cabe en las cuatro y no le roba alto a la pantalla.
 *
 * ⚠️ **El panel se cuelga de `document.body` y no del contenedor.** Las dos barras donde vive el
 * botón —`.nav` del portal y `.ag-barra` del calendario— llevan `backdrop-filter`, y un ancestro con
 * `backdrop-filter` pasa a ser el bloque contenedor de sus descendientes `fixed`: el panel se
 * posicionaría contra la barra y no contra la pantalla. Y `absolute` tampoco sirve: la barra del CRM
 * en móvil es `overflow-x: auto`, y eso vuelve `auto` también el eje vertical, así que lo recortaría.
 */
/**
 * El nombre del evento con el que un panel flotante avisa que se abrió, para que los demás se
 * cierren. Lo emite quien abre —con `detail.origen` apuntando a su propio elemento, que es lo que
 * le permite ignorar su propio aviso— y lo escucha todo el que tenga algo abierto.
 *
 * Existe porque el mecanismo habitual no alcanza: estos paneles se cierran con un clic en
 * `document`, y el botón que abre cada uno tiene que hacer `stopPropagation()` para que ese mismo
 * clic no lo cierre enseguida. El efecto es que abrir uno **no** cierra al otro.
 */
export const PANEL_ABIERTO = 'dv-panel-abierto';

export function montarBotonTema(contenedor, { titulo = 'Modo claro u oscuro' } = {}) {
  if (!contenedor) return;
  contenedor.innerHTML = '';
  contenedor.classList.add('tema-menu');

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'tema-btn';
  btn.title = titulo;
  btn.setAttribute('aria-label', titulo);
  btn.setAttribute('aria-haspopup', 'true');
  btn.setAttribute('aria-expanded', 'false');
  // Clase literal: `fuentes.sh` recorta la fuente grepeando `ti-nombre` en el código.
  btn.innerHTML = '<i class="ti ti-palette" aria-hidden="true"></i>';
  contenedor.appendChild(btn);

  const pop = document.createElement('div');
  pop.className = 'tema-pop';
  pop.hidden = true;
  const cajas = [];
  const fila = (texto, ambito) => {
    const f = document.createElement('div');
    f.className = 'tema-pop-fila';
    const t = document.createElement('span');
    t.textContent = texto;
    const caja = document.createElement('div');
    f.appendChild(t);
    f.appendChild(caja);
    pop.appendChild(f);
    cajas.push([caja, ambito]);
    montarSelectorTema(caja, { ambito });
  };
  // Los dos ámbitos en las cuatro apps. Antes el global sólo se podía tocar desde el portal, así que
  // «todas las apps» era una preferencia que había que ir a cambiar a otra aplicación.
  fila('Todas las apps', 'global');
  fila('Sólo acá', 'app');
  document.body.appendChild(pop);

  // Tocar uno de los dos puede cambiar lo que marca el otro —elegir «Claro» en el global no hace
  // nada si esta app tiene su propia anulación—, así que se redibujan los dos. Sin esto, uno de los
  // controles se queda mostrando el estado anterior.
  pop.addEventListener('tema-cambiado', (e) => {
    for (const [caja, ambito] of cajas) {
      if (caja !== e.target) montarSelectorTema(caja, { ambito });
    }
  });

  const cerrar = () => {
    pop.hidden = true;
    btn.setAttribute('aria-expanded', 'false');
  };
  const abrir = () => {
    const r = btn.getBoundingClientRect();
    // ⚠️ Avisar que se abrió un panel flotante. Todos estos paneles se cierran con un listener de
    // clic en `document`, y **todos los botones que los abren hacen `stopPropagation()`** —si no, el
    // clic que abre uno lo cerraría en el acto—, así que el clic nunca llega a `document` y ninguno
    // cierra al otro: en el portal, tocar el tema y después la campana dejaba el bloque de tema
    // encima de la bandeja, tapándole el título.
    //
    // Va como evento y no llamando al otro por su nombre a propósito: `montarBotonTema` corre en
    // cuatro aplicaciones y sólo una tiene bandeja de notificaciones. Con esto, cualquier panel
    // nuevo se suma escuchando el evento y no hay que volver acá.
    document.dispatchEvent(new CustomEvent(PANEL_ABIERTO, { detail: { origen: pop } }));
    pop.hidden = false;
    // Se ancla al borde derecho del botón y se mete para adentro si no cabe: en un teléfono el botón
    // está pegado al borde y el panel se saldría de la pantalla.
    const ancho = pop.offsetWidth;
    pop.style.top = `${Math.round(r.bottom + 8)}px`;
    pop.style.left = `${Math.round(Math.min(Math.max(8, r.right - ancho), window.innerWidth - ancho - 8))}px`;
    btn.setAttribute('aria-expanded', 'true');
  };
  btn.addEventListener('click', (e) => { e.stopPropagation(); if (pop.hidden) abrir(); else cerrar(); });
  document.addEventListener('click', (e) => { if (!pop.hidden && !pop.contains(e.target)) cerrar(); });
  // Y al revés: si se abre otro panel, éste se va.
  document.addEventListener(PANEL_ABIERTO, (e) => { if (e.detail?.origen !== pop) cerrar(); });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') cerrar(); });
  // Se cierra en vez de reposicionarse: el panel queda anclado a un botón que se movió, y perseguirlo
  // en cada scroll cuesta más de lo que vale un menú que se usa una vez.
  window.addEventListener('scroll', cerrar, true);
  window.addEventListener('resize', cerrar);
}

// Se aplica al importar, antes de que nadie dibuje nada.
aplicarTema();
